import React, { useState, useEffect, useRef, useMemo } from 'react';
import { API_BASE } from '../config';
import { Home, Camera, Filter, X } from 'lucide-react';
import { useTranslation } from '../i18n/LanguageContext';
import LanguageSelector from './LanguageSelector';

const AttendanceAudit = ({ onNavigate }) => {
  const [fetchedAttendances, setFetchedAttendances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    employeeId: '',
    employeeSearch: '',
    withPhoto: false,
    manualOnly: false
  });
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [attendanceModuleType, setAttendanceModuleType] = useState('full');
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  /** full: all columns | simplified/gps_only: hide Face | ultra_simple: hide GPS + Location + Face */
  const showFaceColumn = attendanceModuleType === 'full';
  const showGpsColumn = attendanceModuleType !== 'ultra_simple';
  const showLocationColumn = attendanceModuleType !== 'ultra_simple';
  const showPhotoColumn = attendanceModuleType !== 'gps_only';
  const showWithPhotoFilter = attendanceModuleType !== 'gps_only';
  const showRealPositionColumn = showGpsColumn;
  const visibleColumnCount =
    4 +
    (showGpsColumn ? 1 : 0) +
    (showRealPositionColumn ? 1 : 0) +
    (showLocationColumn ? 1 : 0) +
    (showFaceColumn ? 1 : 0) +
    (showPhotoColumn ? 1 : 0);

  const { t, language } = useTranslation();
  const dateLocale = language === 'fr' ? 'fr-FR' : 'en-US';

  // If a translation key is missing, `t()` returns the key itself (e.g. "attendance_audit.title").
  // This helper prevents showing raw keys on screen.
  const tr = (key, fallback, params = {}) => {
    const v = t(key, params);
    return v === key ? fallback : v;
  };

  const displayAttendances = useMemo(() => {
    let rows = fetchedAttendances;
    if (filters.manualOnly) {
      rows = rows.filter((att) => att.manual_override);
    }
    const empQ = (filters.employeeSearch || '').trim().toLowerCase();
    if (empQ) {
      rows = rows.filter((att) => {
        const fullName = `${att.first_name || ''} ${att.last_name || ''}`.trim().toLowerCase();
        const email = (att.email || '').toLowerCase();
        return fullName.includes(empQ) || email.includes(empQ);
      });
    }
    return rows;
  }, [fetchedAttendances, filters.manualOnly, filters.employeeSearch]);

  useEffect(() => {
    loadAttendances();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await fetch(`${API_BASE}/api/attendance/settings`, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
        });
        const data = await res.json();
        const m = data.settings?.attendanceModuleType;
        if (cancelled) return;
        if (m === 'full' || m === 'simplified' || m === 'ultra_simple' || m === 'gps_only') {
          setAttendanceModuleType(m);
        }
      } catch (e) {
        console.warn('[AUDIT-PHOTOS] attendance module type:', e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const getPhotoUrl = (photoPath) => {
    if (!photoPath) return '';
    return (photoPath.startsWith('http://') || photoPath.startsWith('https://')) ? photoPath : `${API_BASE}${photoPath}`;
  };

  const loadAttendances = async () => {
    const f = filtersRef.current;
    try {
      setLoading(true);
      
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error(tr('attendance_audit.errors.token_missing', 'Access token is missing. Please reconnect.'));
      }
      
      const params = new URLSearchParams();
      if (f.startDate) params.append('startDate', f.startDate);
      if (f.endDate) params.append('endDate', f.endDate);
      if (f.employeeId) params.append('employeeId', f.employeeId);
      if (showWithPhotoFilter && f.withPhoto) params.append('withPhoto', 'true');

      console.log('📸 [AUDIT-PHOTOS] Chargement des pointages avec filtres:', Object.fromEntries(params));

      const response = await fetch(`${API_BASE}/api/attendance/with-photos?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('📸 [AUDIT-PHOTOS] Réponse status:', response.status);

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (parseError) {
          errorData = { error: `Erreur HTTP ${response.status}` };
        }
        console.error('❌ [AUDIT-PHOTOS] Erreur réponse:', errorData);
        throw new Error(tr('attendance_audit.errors.fetch_failed', 'Unable to fetch attendance records'));
      }

      const data = await response.json();
      console.log('📸 [AUDIT-PHOTOS] Données reçues:', data);
      if (data.attendances && data.attendances.length > 0) {
        console.log('📸 [AUDIT-PHOTOS] Exemple photo_path:', data.attendances[0].photo_path);
        console.log('📸 [AUDIT-PHOTOS] API_BASE:', API_BASE);
        console.log('📸 [AUDIT-PHOTOS] URL photo complète:', `${API_BASE}${data.attendances[0].photo_path || ''}`);
      }

      // Accepter la réponse si success n'est pas explicitement false
      // ou si attendances existe (même vide)
      if (data.success !== false || data.attendances !== undefined) {
        const results = data.attendances || [];
        console.log(`✅ [AUDIT-PHOTOS] ${results.length} pointages chargés (API)`);
        setFetchedAttendances(results);
      } else {
        // Si success est false mais pas d'erreur explicite, utiliser un message générique
        throw new Error(tr('attendance_audit.errors.fetch_failed', 'Unable to fetch attendance records'));
      }
    } catch (error) {
      console.error('❌ [AUDIT-PHOTOS] Erreur chargement:', error);
      const errorMessage = error.message || tr('attendance_audit.errors.unknown_error', 'Unknown error');
      alert('❌ ' + errorMessage);
      setFetchedAttendances([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div key={language} style={{
      minHeight: '100vh',
      width: '100%',
      background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 100%)',
      color: '#ffffff',
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
      overflowX: 'hidden',
      paddingBottom: 'env(safe-area-inset-bottom, 0)',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      overflowY: 'auto'
    }}>
      {/* Header transparent */}
      <style>{`
        @media (max-width: 640px) {
          /* On mobile: header scrolls with page (not sticky) */
          .audit-header {
            position: relative !important;
            top: auto !important;
            left: auto !important;
            right: auto !important;
            padding: 20px 16px 16px !important;
          }
          /* Content needs no extra top padding since header is no longer fixed */
          .audit-content {
            padding-top: 16px !important;
          }
          /* Language selector stays in header on mobile (header scrolls anyway) */
          .audit-lang-selector-header {
            display: flex !important;
            width: 100%;
            margin-top: 8px;
            justify-content: flex-end;
          }
          /* Hide the duplicate content-area language selector on mobile */
          .audit-lang-selector-content { display: none; }
          .audit-type-col {
            min-width: 130px;
            width: 130px;
          }
          .audit-type-badge {
            white-space: nowrap;
            padding: 6px 18px !important;
          }
        }
        @media (min-width: 641px) {
          /* Show language selector inline in the header on desktop */
          .audit-lang-selector-header {
            margin-left: auto;
            flex-shrink: 0;
            align-self: center;
          }
          /* Hide the content-area language selector on desktop */
          .audit-lang-selector-content { display: none; }
        }
      `}</style>
      <div className="audit-header" style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        padding: '24px 20px 20px',
        background: 'transparent',
        backdropFilter: 'none',
        borderBottom: 'none',
        zIndex: 999,
        maxWidth: '100%'
      }}>
        <div style={{ 
          display: 'flex', 
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          marginBottom: '6px',
          gap: '16px'
        }}>
          {onNavigate && (
            <button
              onClick={() => onNavigate('home')}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                color: '#ffffff',
                padding: '10px',
                borderRadius: '12px',
                fontSize: '18px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s',
                backdropFilter: 'blur(10px)',
                flexShrink: 0,
                width: '44px',
                height: '44px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
              title={tr('attendance_audit.back_home', 'Back to home')}
            >
              <Home size={20} />
            </button>
          )}
          <div style={{ 
            fontSize: 'clamp(28px, 5vw, 40px)', 
            fontWeight: 700, 
            maxWidth: '100%',
            letterSpacing: '-0.5px',
            lineHeight: 1.2,
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <Camera size={32} />
            {tr('attendance_audit.title', 'Audit Photos')}
          </div>
          {/* Language selector — desktop only (hidden on mobile via CSS) */}
          <div className="audit-lang-selector-header">
            <LanguageSelector />
          </div>
        </div>
        <p style={{
          fontSize: 'clamp(14px, 2vw, 16px)',
          color: 'rgba(255, 255, 255, 0.7)',
          margin: '8px 0 0 0',
          letterSpacing: '0.2px',
          fontWeight: 500
        }}>
          {attendanceModuleType === 'gps_only'
            ? 'Verification of GPS attendance records'
            : tr('attendance_audit.subtitle', 'Verification of attendance with photos')}
        </p>
      </div>

      {/* Contenu principal */}
      <div className="audit-content" style={{
        paddingTop: '140px',
        paddingBottom: '40px',
        paddingLeft: '20px',
        paddingRight: '20px',
        maxWidth: '1600px',
        margin: '0 auto'
      }}>
        {/* Language selector — mobile only, shown at top of content (not in fixed header) */}
        <div className="audit-lang-selector-content">
          <LanguageSelector />
        </div>
      {/* FILTRES */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '20px',
        padding: '24px',
        marginBottom: '24px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)'
      }}>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: 600,
              fontSize: '13px',
              color: 'rgba(255, 255, 255, 0.8)'
            }}>
              {tr('attendance_audit.start_date_label', 'Start date')}
            </label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters({...filters, startDate: e.target.value})}
              style={{ 
                padding: '12px 16px', 
                borderRadius: '12px', 
                border: '1px solid rgba(255, 255, 255, 0.2)',
                background: 'rgba(255, 255, 255, 0.1)',
                color: '#ffffff',
                fontSize: '14px',
                outline: 'none'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.4)';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
            />
          </div>

          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: 600,
              fontSize: '13px',
              color: 'rgba(255, 255, 255, 0.8)'
            }}>
              {tr('attendance_audit.end_date_label', 'End date')}
            </label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters({...filters, endDate: e.target.value})}
              style={{ 
                padding: '12px 16px', 
                borderRadius: '12px', 
                border: '1px solid rgba(255, 255, 255, 0.2)',
                background: 'rgba(255, 255, 255, 0.1)',
                color: '#ffffff',
                fontSize: '14px',
                outline: 'none'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.4)';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
            />
          </div>

          <div style={{ flex: '1 1 200px', minWidth: 'min(100%, 220px)', maxWidth: '320px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: 600,
              fontSize: '13px',
              color: 'rgba(255, 255, 255, 0.8)'
            }}>
              {tr('attendance_audit.employee_search_label', 'Employee')}
            </label>
            <input
              type="text"
              value={filters.employeeSearch}
              onChange={(e) => setFilters({ ...filters, employeeSearch: e.target.value })}
              placeholder={tr('attendance_audit.employee_search_placeholder', 'Name or email…')}
              autoComplete="off"
              style={{ 
                width: '100%',
                boxSizing: 'border-box',
                padding: '12px 16px', 
                borderRadius: '12px', 
                border: '1px solid rgba(255, 255, 255, 0.2)',
                background: 'rgba(255, 255, 255, 0.1)',
                color: '#ffffff',
                fontSize: '14px',
                outline: 'none'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.4)';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {showWithPhotoFilter && (
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px', 
                cursor: 'pointer',
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: '14px'
              }}>
                <input
                  type="checkbox"
                  checked={filters.withPhoto}
                  onChange={(e) => setFilters({...filters, withPhoto: e.target.checked})}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span>{tr('attendance_audit.with_photo_only', 'With photo only')}</span>
              </label>
            )}
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px', 
              cursor: 'pointer',
              color: 'rgba(255, 255, 255, 0.9)',
              fontSize: '14px'
            }}>
              <input
                type="checkbox"
                checked={filters.manualOnly}
                onChange={(e) => setFilters({...filters, manualOnly: e.target.checked})}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
              <span>{tr('attendance_audit.manual_only_label', '⚠️ Manual attendance only')}</span>
            </label>
          </div>

          <button
            onClick={loadAttendances}
            style={{
              padding: '12px 24px',
              background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
              color: '#ffffff',
              border: 'none',
              borderRadius: '12px',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(59, 130, 246, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
            }}
          >
            <Filter size={18} />
            {tr('attendance_audit.search', 'Search')}
          </button>
        </div>
      </div>

      {/* TABLEAU */}
      {loading ? (
        <div style={{
          textAlign: 'center',
          padding: '80px 20px',
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '20px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.2)'
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '16px' }}>⏳</div>
          <div style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '16px' }}>{tr('attendance_audit.loading', 'Loading...')}</div>
        </div>
      ) : (
        <>
        {showGpsColumn && (
        <div style={{ marginBottom: '16px' }}>
          <div style={{
            background: 'rgba(59, 130, 246, 0.1)',
            border: '1px solid rgba(59, 130, 246, 0.3)',
            borderRadius: '12px',
            padding: '12px 16px',
            fontSize: '13px',
            color: 'rgba(255, 255, 255, 0.85)',
            lineHeight: 1.5
          }}>
            <strong style={{ color: '#93c5fd' }}>{tr('attendance_audit.gps_zone_help_title', 'GPS / Zone in this table')}</strong>
            <div style={{ whiteSpace: 'pre-line' }}>
              {tr('attendance_audit.gps_zone_help_body', 'Distance and zone labels are saved at clock-in time.')}
            </div>
          </div>
        </div>
        )}
        <div className="audit-table-wrapper" style={{
          overflowX: 'auto',
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '20px',
          padding: '20px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)'
        }}>
          <table className="audit-table" style={{
            width: '100%',
            borderCollapse: 'collapse',
            color: '#ffffff'
          }}>
            <thead>
              <tr style={{ background: 'rgba(255, 255, 255, 0.1)' }}>
                <th style={{ 
                  padding: '16px', 
                  textAlign: 'left', 
                  borderBottom: '2px solid rgba(255, 255, 255, 0.2)',
                  color: '#ffffff',
                  fontWeight: 600,
                  fontSize: '14px'
                }}>
                  {tr('attendance_audit.table.date_time', 'Date/Time')}
                </th>
                <th style={{ 
                  padding: '16px', 
                  textAlign: 'left', 
                  borderBottom: '2px solid rgba(255, 255, 255, 0.2)',
                  color: '#ffffff',
                  fontWeight: 600,
                  fontSize: '14px'
                }}>
                  {tr('attendance_audit.table.employee', 'Employee')}
                </th>
                <th className="audit-type-col" style={{ 
                  padding: '16px', 
                  textAlign: 'center', 
                  borderBottom: '2px solid rgba(255, 255, 255, 0.2)',
                  color: '#ffffff',
                  fontWeight: 600,
                  fontSize: '14px'
                }}>
                  {tr('attendance_audit.table.type', 'Type')}
                </th>
                {showGpsColumn && (
                <th style={{ 
                  padding: '16px', 
                  textAlign: 'center', 
                  borderBottom: '2px solid rgba(255, 255, 255, 0.2)',
                  color: '#ffffff',
                  fontWeight: 600,
                  fontSize: '14px'
                }}>
                  {tr('attendance_audit.table.gps', 'GPS')}
                </th>
                )}
                {showLocationColumn && (
                <th style={{ 
                  padding: '16px', 
                  textAlign: 'left', 
                  borderBottom: '2px solid rgba(255, 255, 255, 0.2)',
                  color: '#ffffff',
                  fontWeight: 600,
                  fontSize: '14px'
                }}>
                  {tr('attendance_audit.table.location', 'Nearest authorised location')}
                </th>
                )}
                {showRealPositionColumn && (
                <th style={{ 
                  padding: '16px', 
                  textAlign: 'center', 
                  borderBottom: '2px solid rgba(255, 255, 255, 0.2)',
                  color: '#ffffff',
                  fontWeight: 600,
                  fontSize: '14px'
                }}>
                  {tr('attendance_audit.table.real_position', 'Real position')}
                </th>
                )}
                {showFaceColumn && (
                <th style={{ 
                  padding: '16px', 
                  textAlign: 'center', 
                  borderBottom: '2px solid rgba(255, 255, 255, 0.2)',
                  color: '#ffffff',
                  fontWeight: 600,
                  fontSize: '14px'
                }}>
                  {tr('attendance_audit.table.face', 'Face')}
                </th>
                )}
                <th style={{ 
                  padding: '16px', 
                  textAlign: 'center', 
                  borderBottom: '2px solid rgba(255, 255, 255, 0.2)',
                  color: '#ffffff',
                  fontWeight: 600,
                  fontSize: '14px'
                }}>
                  {tr('attendance_audit.table.mode', 'Mode')}
                </th>
                {showPhotoColumn && (
                <th style={{ 
                  padding: '16px', 
                  textAlign: 'center', 
                  borderBottom: '2px solid rgba(255, 255, 255, 0.2)',
                  color: '#ffffff',
                  fontWeight: 600,
                  fontSize: '14px'
                }}>
                  {tr('attendance_audit.table.photo', 'Photo')}
                </th>
                )}
              </tr>
            </thead>
            <tbody>
              {displayAttendances.length === 0 ? (
                <tr>
                  <td colSpan={visibleColumnCount} style={{ 
                    padding: '60px 20px', 
                    textAlign: 'center', 
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontSize: '16px'
                  }}>
                    {tr('attendance_audit.no_records', 'No attendance found')}
                  </td>
                </tr>
              ) : (
                displayAttendances.map(att => (
                  <tr key={att.id} style={{
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                    background: att.manual_override 
                      ? 'rgba(255, 193, 7, 0.15)' 
                      : 'rgba(255, 255, 255, 0.03)',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = att.manual_override 
                      ? 'rgba(255, 193, 7, 0.25)' 
                      : 'rgba(255, 255, 255, 0.08)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = att.manual_override 
                      ? 'rgba(255, 193, 7, 0.15)' 
                      : 'rgba(255, 255, 255, 0.03)';
                  }}>
                    <td style={{ padding: '16px', color: 'rgba(255, 255, 255, 0.9)' }}>
                      {new Date(att.timestamp).toLocaleString(dateLocale)}
                    </td>
                    <td style={{ padding: '16px' }}>
                      <div style={{ fontWeight: 600, color: '#ffffff', marginBottom: '4px' }}>
                        {att.first_name} {att.last_name}
                      </div>
                      <div style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.6)' }}>
                        {att.email}
                      </div>
                    </td>
                    <td className="audit-type-col" style={{ padding: '16px', textAlign: 'center' }}>
                      <span className="audit-type-badge" style={{
                        padding: '6px 14px',
                        borderRadius: '12px',
                        fontSize: '13px',
                        fontWeight: 600,
                        background: att.type === 'in' 
                          ? 'rgba(16, 185, 129, 0.2)' 
                          : 'rgba(239, 68, 68, 0.2)',
                        color: att.type === 'in' ? '#10b981' : '#ef4444',
                        border: `1px solid ${att.type === 'in' ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
                        display: 'inline-block'
                      }}>
                        {att.type === 'in' ? tr('attendance_audit.type_in', '🏢 Arrival') : tr('attendance_audit.type_out', '🚪 Departure')}
                      </span>
                    </td>
                    {showGpsColumn && (
                    <td style={{ padding: '16px', textAlign: 'center' }}>
                      <div style={{ fontSize: '14px', fontWeight: 600, color: '#ffffff', marginBottom: '4px' }}>
                        {att.distance != null && Number.isFinite(Number(att.distance)) ? `${Math.round(Number(att.distance))}m` : '—'}
                      </div>
                      <div style={{ 
                        fontSize: '12px', 
                        color: att.within_radius ? '#10b981' : '#ef4444',
                        fontWeight: 500
                      }}>
                        {att.within_radius ? tr('attendance_audit.in_zone', '✅ In zone') : tr('attendance_audit.out_zone', '❌ Out of zone')}
                      </div>
                    </td>
                    )}
                    {showLocationColumn && (
                    <td style={{ padding: '16px', color: 'rgba(255, 255, 255, 0.9)' }}>
                      {att.location_name ? (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          📍 {att.location_name}
                        </span>
                      ) : (
                        <span style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '13px' }}>—</span>
                      )}
                    </td>
                    )}
                    {showRealPositionColumn && (
                    <td style={{ padding: '16px', textAlign: 'center', verticalAlign: 'middle' }}>
                      {(() => {
                        const lat = att.latitude != null ? Number(att.latitude) : NaN;
                        const lon = att.longitude != null ? Number(att.longitude) : NaN;
                        const hasCoords = Number.isFinite(lat) && Number.isFinite(lon);
                        if (!hasCoords) {
                          return <span style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '13px' }}>—</span>;
                        }
                        const gmapsUrl = `https://www.google.com/maps?q=${encodeURIComponent(`${lat},${lon}`)}`;
                        return (
                          <a
                            href={gmapsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              display: 'inline-block',
                              padding: '8px 14px',
                              background: 'linear-gradient(135deg, #4285f4 0%, #34a853 100%)',
                              color: '#ffffff',
                              borderRadius: '10px',
                              fontSize: '12px',
                              fontWeight: 600,
                              textDecoration: 'none',
                              boxShadow: '0 2px 8px rgba(66, 133, 244, 0.35)',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {tr('attendance_audit.view_google_maps', 'View on Google Maps')}
                          </a>
                        );
                      })()}
                    </td>
                    )}
                    {showFaceColumn && (
                    <td style={{ padding: '16px', textAlign: 'center', fontSize: '1.5rem' }}>
                      {att.face_detected ? '✅' : '❌'}
                    </td>
                    )}
                    <td style={{ padding: '16px', textAlign: 'center' }}>
                      {att.manual_override ? (
                        <div>
                          <div style={{ fontWeight: 600, color: '#ffc107', fontSize: '13px' }}>
                            {tr('attendance_audit.manual', '⚠️ Manual')}
                          </div>
                          {att.override_reason && (
                            <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)', marginTop: '4px' }}>
                              {att.override_reason}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span style={{ color: '#10b981', fontWeight: 600, fontSize: '13px' }}>
                          {tr('attendance_audit.auto', '✅ Auto')}
                        </span>
                      )}
                    </td>
                    {showPhotoColumn && (
                    <td style={{ padding: '16px', textAlign: 'center' }}>
                      {att.photo_path ? (
                        <div>
                          <img
                            className="audit-photo-img"
                            src={getPhotoUrl(att.photo_path)}
                            alt={tr('attendance_audit.photo_alt', 'Attendance record')}
                            style={{
                              width: '80px',
                              height: '80px',
                              objectFit: 'cover',
                              borderRadius: '12px',
                              cursor: 'pointer',
                              border: '2px solid rgba(255, 255, 255, 0.2)',
                              transition: 'all 0.2s',
                              backgroundColor: 'rgba(255, 255, 255, 0.05)'
                            }}
                            onClick={() => setSelectedPhoto(getPhotoUrl(att.photo_path))}
                            onError={(e) => {
                              console.error('❌ [AUDIT-PHOTOS] Erreur chargement image:', getPhotoUrl(att.photo_path));
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'block';
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'scale(1.05)';
                              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.4)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'scale(1)';
                              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                            }}
                          />
                          <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)', marginTop: '6px', display: 'none' }}>
                            {tr('attendance_audit.photo_missing', 'No photo')}
                          </div>
                          <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)', marginTop: '6px' }}>
                            {att.photo_taken_at && new Date(att.photo_taken_at).toLocaleTimeString(dateLocale)}
                          </div>
                        </div>
                      ) : (
                        <span style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px' }}>
                          {tr('attendance_audit.photo_missing', 'No photo')}
                        </span>
                      )}
                    </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>

          <div style={{ 
            marginTop: '24px', 
            textAlign: 'center', 
            color: 'rgba(255, 255, 255, 0.8)',
            fontSize: '14px',
            fontWeight: 500
          }}>
            {(() => {
              const key = 'attendance_audit.total_records';
              const v = t(key, { count: displayAttendances.length });
              return v === key ? `Total: ${displayAttendances.length} record(s)` : v;
            })()}
            {filters.manualOnly && (
              <span style={{ marginLeft: '10px', color: '#ffc107', fontWeight: 600 }}>
                {tr('attendance_audit.manual_only_note', '(⚠️ Manual attendance only)')}
              </span>
            )}
          </div>
        </div>
        </>
      )}

      {/* MODAL PHOTO AGRANDIE */}
      {selectedPhoto && (
        <div
          onClick={() => setSelectedPhoto(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.95)',
            backdropFilter: 'blur(10px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            cursor: 'pointer'
          }}
        >
          <div style={{
            position: 'relative',
            maxWidth: '90%',
            maxHeight: '90%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <img
              src={selectedPhoto}
              alt={tr('attendance_audit.photo_zoom_alt', 'Enlarged view')}
              style={{
                maxWidth: '100%',
                maxHeight: '90vh',
                borderRadius: '16px',
                boxShadow: '0 20px 60px rgba(0,0,0,0.8)'
              }}
            />
            <button
              onClick={(e) => {
                e.stopPropagation();
                setSelectedPhoto(null);
              }}
              style={{
                position: 'absolute',
                top: '20px',
                right: '20px',
                background: 'rgba(255, 255, 255, 0.2)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                color: '#ffffff',
                width: '44px',
                height: '44px',
                borderRadius: '12px',
                fontSize: '24px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
              }}
            >
              <X size={24} />
            </button>
          </div>
        </div>
      )}
      </div>

      {/* CSS pour animations */}
      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @media (min-width: 768px) {
          .audit-content {
            padding: 160px 40px 40px !important;
          }
        }
        @media (min-width: 1024px) {
          .audit-content {
            padding: 180px 60px 60px !important;
          }
        }
        .audit-content {
          animation: slideUp 0.4s ease-out;
        }

        .audit-table-wrapper {
          -webkit-overflow-scrolling: touch;
          width: 100%;
          max-width: 100%;
          padding: 16px !important;
        }

        .audit-table {
          width: 100%;
          table-layout: fixed;
        }

        .audit-table th,
        .audit-table td {
          padding: 12px !important;
          font-size: 13px !important;
          word-break: break-word;
          overflow-wrap: anywhere;
        }

        @media (max-width: 640px) {
          .audit-table-wrapper {
            padding: 12px !important;
          }

          .audit-table {
            min-width: 980px;
          }

          .audit-table th,
          .audit-table td {
            padding: 10px !important;
            font-size: 12px !important;
          }

          .audit-table td {
            word-break: break-word;
          }

          .audit-photo-img {
            width: 56px !important;
            height: 56px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default AttendanceAudit;




